/**
 * Calculate the last page of a pagination component
 *
 * @method calculateLastPage
 * @param  {number}          count        The total count of items
 * @param  {number}          itemsPerPage The amount of items per page
 * @return {number}                       The calculated last page
 */
export const calculateLastPage = (count, itemsPerPage) =>
    Math.max(0, Math.ceil(count / itemsPerPage)) || 1;

/**
 * Adjust the showcaseCount based on the number of digits in the last page number.
 * Reduces visible page buttons to prevent pagination overflow in constrained containers.
 *
 * @method getAdjustedShowcaseCount
 * @param  {number} baseCount The original showcaseCount
 * @param  {number} lastPage  The total number of pages
 * @return {number}           The adjusted showcaseCount
 */
export const getAdjustedShowcaseCount = (baseCount, lastPage) => {
    if (lastPage <= 0 || baseCount <= 1) return baseCount;
    const digits = String(lastPage).length;
    if (digits <= 2) return baseCount;
    const reduction = digits - 2;
    return Math.max(2, baseCount - reduction);
};

/**
 * Sentinel value returned by `getPaginationRange` in place of a hidden page
 * span. Consumers render it as the "…" ellipsis glyph.
 *
 * @type {string}
 */
export const PAGINATION_ELLIPSIS = 'ellipsis';

/**
 * Build the list of page-number items to display for a windowed pagination
 * control. Following the Banner Screen redesign (GN-37779) Figma, up to
 * `boundaryCount` pages are always shown at each end (e.g. `1 2 3 4 … 11 12 13
 * 14`) together with a `siblingCount` window around the current page. Gaps
 * wider than a single page are collapsed to a `PAGINATION_ELLIPSIS` marker,
 * while a single-page gap is filled with the page itself (so a lone "…" is
 * never shown for one page). When the ranges overlap, every page is listed and
 * no ellipsis is rendered.
 *
 * @method getPaginationRange
 * @param  {number}                 currentPage      The active page (1-based)
 * @param  {number}                 lastPage         The final page number
 * @param  {number}                 [boundaryCount=4] Pages shown at each end
 * @param  {number}                 [siblingCount=1]  Pages shown around current
 * @return {Array<number|string>}                    Page numbers and ellipsis markers
 */
export const getPaginationRange = (currentPage, lastPage, boundaryCount = 4, siblingCount = 1) => {
    const total = Math.max(0, Math.floor(lastPage) || 0);

    if (total <= 1) {
        return total === 1 ? [1] : [];
    }

    const current = Math.min(Math.max(1, Math.floor(currentPage) || 1), total);
    const boundary = Math.max(1, Math.floor(boundaryCount) || 1);
    const sibling = Math.max(0, Math.floor(siblingCount) || 0);

    const pageSet = new Set();

    for (let page = 1; page <= Math.min(boundary, total); page += 1) {
        pageSet.add(page);
    }
    for (let page = Math.max(total - boundary + 1, 1); page <= total; page += 1) {
        pageSet.add(page);
    }
    for (let page = current - sibling; page <= current + sibling; page += 1) {
        if (page >= 1 && page <= total) {
            pageSet.add(page);
        }
    }

    const pages = Array.from(pageSet).sort((a, b) => a - b);

    const range = [];
    let previous = 0;

    pages.forEach(page => {
        if (previous) {
            if (page - previous === 2) {
                range.push(previous + 1);
            } else if (page - previous > 2) {
                range.push(PAGINATION_ELLIPSIS);
            }
        }
        range.push(page);
        previous = page;
    });

    return range;
};
